'use client';

import { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  ExternalLink,
  Mail,
  Map,
  MapPin,
  Navigation,
  Phone,
  Share2,
  type LucideIcon,
} from 'lucide-react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'motion/react';
import { Stagger, StaggerChild } from '@/components/motion/InView';
import type { OpeningHoursBundle } from '@restaurant/db';
import { Link } from '@/i18n/navigation';
import styles from './ContactPageView.module.css';

/* ─── Types ──────────────────────────────────────────────────────────────── */

interface ContactCard {
  id: string;
  icon: 'phone' | 'mail' | 'map-pin' | 'share-2';
  title: string;
  value?: string;
  href?: string;
  links?: readonly { id: string; label: string; href: string }[];
}

interface ContactPageViewProps {
  data: {
    locale: string;
    quickSectionAriaLabel: string;
    quickCards: readonly ContactCard[];
    story: { eyebrow: string; title: string; description: string };
    hours: {
      title: string;
      todayLabel: string;
      statusLabel: string;
      statusDetail: string;
      isOpen?: boolean;
      message: string | null;
      rows: { dayOfWeek: string; day: string; value: string; isClosed: boolean }[];
    };
    map: {
      embedUrl: string;
      googleMapsUrl: string;
      googleMapsLabel: string;
      wazeUrl: string;
      wazeLabel: string;
    };
    form: {
      title: string;
      nameLabel: string;
      namePlaceholder: string;
      emailLabel: string;
      emailPlaceholder: string;
      subjectLabel: string;
      subjectOptions: readonly string[];
      messageLabel: string;
      messagePlaceholder: string;
      submitLabel: string;
      successMessage: string;
    };
    cta: {
      title: string;
      links: readonly { id: string; href: '/' | '/menu' | '/commande-a-emporter' | '/en-ce-moment'; label: string }[];
    };
  };
}

function normalizeHours(hours: ContactPageViewProps['data']['hours']): ContactPageViewProps['data']['hours'] {
  if (Array.isArray(hours.rows)) return hours;

  return {
    ...hours,
    statusLabel: hours.statusLabel || 'Horaires',
    statusDetail: hours.statusDetail || '',
    message: hours.message || null,
    rows: [],
  };
}

/* ─── Constants ──────────────────────────────────────────────────────────── */

const EASE = [0.22, 1, 0.36, 1] as const;
const INITIAL_REFRESH_DELAY_MS = 15_000;
const REFRESH_INTERVAL_MS = 30_000;
const QUICK_CARD_ICONS: Record<ContactCard['icon'], LucideIcon> = {
  phone: Phone,
  mail: Mail,
  'map-pin': MapPin,
  'share-2': Share2,
};

/* ─── Quick Card ─────────────────────────────────────────────────────────── */

function QuickCard({ card }: { card: ContactCard }) {
  const Icon = QUICK_CARD_ICONS[card.icon];

  return (
    <motion.article
      className={styles.quickCard}
      whileHover={{ y: -6, transition: { type: 'spring', stiffness: 320, damping: 24 } }}
    >
      <div className={styles.quickCardInner}>

        {/* Ligne 1 — icône + label, hauteur fixe 7.5rem */}
        <div className={styles.quickCardTop}>
          <motion.div
            className={styles.quickIconCircle}
            whileHover={{ scale: 1.08, transition: { type: 'spring', stiffness: 380, damping: 22 } }}
          >
            <Icon className={styles.quickIcon} aria-hidden="true" strokeWidth={1.8} />
          </motion.div>
          <h2 className={styles.quickLabel}>{card.title}</h2>
        </div>

        {/* Ligne 2 — séparateur doré */}
        <div className={styles.quickCardSep} aria-hidden="true" />

        {/* Ligne 3 — valeur, démarre au même Y sur toutes les cards */}
        <div className={styles.quickCardValueArea}>
          {card.value ? (
            card.href ? (
              <a
                href={card.href}
                className={`${styles.quickValueLink} ${card.id === 'address' ? styles.multilineValue : ''}`}
                target={card.id === 'address' ? '_blank' : undefined}
                rel={card.id === 'address' ? 'noreferrer' : undefined}
              >
                {card.value}
              </a>
            ) : (
              <p className={`${styles.quickValue} ${card.id === 'address' ? styles.multilineValue : ''}`}>
                {card.value}
              </p>
            )
          ) : (
            <div className={styles.socialLinks}>
              {card.links?.map((link) => (
                <a key={link.id} href={link.href} target="_blank" rel="noreferrer" className={styles.socialLink}>
                  <span>{link.label}</span>
                  <ExternalLink className={styles.socialLinkIcon} aria-hidden="true" strokeWidth={1.8} />
                </a>
              ))}
            </div>
          )}
        </div>

      </div>
    </motion.article>
  );
}

/* ─── Animated field wrapper ─────────────────────────────────────────────── */

function FieldGroup({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      className={styles.fieldGroup}
      initial={{ opacity: 0, y: 18 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

/* ─── Hours block ────────────────────────────────────────────────────────── */

const DAY_INDEX_TO_VALUE = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

function HoursBlock({ hours }: { hours: ContactPageViewProps['data']['hours'] }) {
  const [todayLabel, setTodayLabel] = useState<string | null>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    setTodayLabel(DAY_INDEX_TO_VALUE[new Date().getDay()] ?? null);
  }, []);

  const isOpen = hours.isOpen ?? /ouvert/i.test(hours.statusLabel);
  const detail = hours.message || hours.statusDetail;

  return (
    <div className={styles.hoursBlock}>
      <div className={styles.hoursHeader}>
        <Clock3 className={styles.hoursHeaderIcon} aria-hidden="true" strokeWidth={1.8} />
        <h3 className={styles.hoursLabel}>{hours.title}</h3>
        <span className={styles.hoursHeaderRule} aria-hidden="true" />
      </div>

      <div
        className={`${styles.hoursStatus} ${isOpen ? styles.hoursStatusOpen : styles.hoursStatusClosed}`}
        role="status"
      >
        <span className={styles.hoursStatusDot} aria-hidden="true">
          <span className={styles.hoursStatusDotPulse} />
          <span className={styles.hoursStatusDotCore} />
        </span>
        <div className={styles.hoursStatusText}>
          <strong>{hours.statusLabel}</strong>
          {detail && <span>{detail}</span>}
        </div>
      </div>

      <ul className={styles.hoursList}>
        {hours.rows.map((row, index) => {
          const isToday = todayLabel === row.dayOfWeek;
          const Item = (
            <>
              <span className={styles.hoursDay}>
                {row.day}
                {isToday && <span className={styles.hoursTodayTag}>{hours.todayLabel}</span>}
              </span>
              <span className={styles.hoursDots} aria-hidden="true" />
              <span
                className={`${styles.hoursValue} ${row.isClosed ? styles.hoursValueClosed : ''}`}
              >
                {row.value}
              </span>
            </>
          );

          return (
            <motion.li
              key={row.day}
              className={`${styles.hoursRow} ${isToday ? styles.hoursRowToday : ''} ${
                row.isClosed ? styles.hoursRowClosed : ''
              }`}
              initial={reduced ? false : { opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, delay: index * 0.05, ease: EASE }}
            >
              {Item}
            </motion.li>
          );
        })}
      </ul>
    </div>
  );
}

/* ─── Info section ───────────────────────────────────────────────────────── */

function InfoSection({
  story,
  hours,
  map,
}: {
  story: ContactPageViewProps['data']['story'];
  hours: ContactPageViewProps['data']['hours'];
  map: ContactPageViewProps['data']['map'];
}) {
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const leftInView = useInView(leftRef, { once: true, margin: '-80px' });
  const rightInView = useInView(rightRef, { once: true, margin: '-80px' });
  const normalizedHours = normalizeHours(hours);

  return (
    <section className={styles.infoSection}>
      <div className={styles.infoGrid}>
        <motion.div
          ref={leftRef}
          className={styles.storyBlock}
          initial={{ opacity: 0, x: -32 }}
          animate={leftInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.88, ease: EASE }}
        >
          <div className={styles.storyAccent} aria-hidden="true" />

          <span className={styles.storyEyebrow}>
            <span className={styles.storyEyebrowDash} aria-hidden="true" />
            {story.eyebrow}
            <span className={styles.storyEyebrowDash} aria-hidden="true" />
          </span>

          <h2 className={styles.sectionTitle}>{story.title}</h2>

          <div className={styles.storyDivider} aria-hidden="true">
            <span className={styles.storyDividerLine} />
            <svg
              className={styles.storyDividerMark}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2 L13.6 10.4 L22 12 L13.6 13.6 L12 22 L10.4 13.6 L2 12 L10.4 10.4 Z"
                fill="currentColor"
              />
            </svg>
            <span className={styles.storyDividerLine} />
          </div>

          <div className={styles.storyParagraphs}>
            {story.description
              .split(/(?<=\.)\s+/)
              .filter(Boolean)
              .map((paragraph, index) => (
                <p
                  key={index}
                  className={`${styles.sectionText} ${index === 0 ? styles.sectionTextLead : ''}`}
                >
                  {paragraph}
                </p>
              ))}
          </div>

          <div className={styles.storySignature} aria-hidden="true">
            <span className={styles.storySignatureLine} />
            <span className={styles.storySignatureMark}>Le B · Brasserie</span>
          </div>

          <HoursBlock hours={normalizedHours} />
        </motion.div>

        <motion.div
          ref={rightRef}
          className={styles.mapCard}
          initial={{ opacity: 0, x: 32 }}
          animate={rightInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.88, delay: 0.08, ease: EASE }}
        >
          <div className={styles.mapFrame}>
            <iframe
              src={map.embedUrl}
              className={styles.mapIframe}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={map.googleMapsLabel}
            />
          </div>

          <div className={styles.mapActions}>
            <a href={map.googleMapsUrl} target="_blank" rel="noreferrer" className={styles.mapAction}>
              <Map className={styles.mapActionIcon} aria-hidden="true" strokeWidth={1.8} />
              {map.googleMapsLabel}
            </a>
            <a href={map.wazeUrl} target="_blank" rel="noreferrer" className={styles.mapAction}>
              <Navigation className={styles.mapActionIcon} aria-hidden="true" strokeWidth={1.8} />
              {map.wazeLabel}
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Form section ───────────────────────────────────────────────────────── */

function FormSection({ form }: { form: ContactPageViewProps['data']['form'] }) {
  const [sent, setSent] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, { once: true, margin: '-60px' });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSent(true);
  }

  return (
    <section className={styles.formSection}>
      <motion.div
        ref={titleRef}
        className={styles.formIntro}
        initial={{ opacity: 0, y: 22 }}
        animate={titleInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.72, ease: EASE }}
      >
        <h2 className={styles.formTitle}>{form.title}</h2>
        <motion.div
          className={styles.formDivider}
          aria-hidden="true"
          initial={{ scaleX: 0 }}
          animate={titleInView ? { scaleX: 1 } : {}}
          transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
        />
      </motion.div>

      <AnimatePresence mode="wait">
        {sent ? (
          <motion.div
            key="success"
            className={styles.successState}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
          transition={{ duration: 0.55, ease: EASE }}
        >
            <CheckCircle2 className={styles.successIcon} aria-hidden="true" strokeWidth={1.6} />
            <p className={styles.successText}>{form.successMessage}</p>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            className={styles.formGrid}
            onSubmit={handleSubmit}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className={styles.formColumn}>
              <FieldGroup>
                <label className={styles.fieldLabel} htmlFor="contact-name">{form.nameLabel}</label>
                <div className={styles.fieldWrap}>
                  <input id="contact-name" type="text" className={styles.field} placeholder={form.namePlaceholder} />
                  <span className={styles.fieldLine} aria-hidden="true" />
                </div>
              </FieldGroup>

              <FieldGroup>
                <label className={styles.fieldLabel} htmlFor="contact-email">{form.emailLabel}</label>
                <div className={styles.fieldWrap}>
                  <input id="contact-email" type="email" className={styles.field} placeholder={form.emailPlaceholder} />
                  <span className={styles.fieldLine} aria-hidden="true" />
                </div>
              </FieldGroup>

              <FieldGroup>
                <label className={styles.fieldLabel} htmlFor="contact-subject">{form.subjectLabel}</label>
                <div className={styles.fieldWrap}>
                  <select id="contact-subject" className={styles.field}>
                    {form.subjectOptions.map((option) => (
                      <option key={option}>{option}</option>
                    ))}
                  </select>
                  <span className={styles.fieldLine} aria-hidden="true" />
                </div>
              </FieldGroup>
            </div>

            <div className={styles.messageColumn}>
              <FieldGroup>
                <label className={styles.fieldLabel} htmlFor="contact-message">{form.messageLabel}</label>
                <div className={styles.fieldWrap}>
                  <textarea
                    id="contact-message"
                    className={`${styles.field} ${styles.textarea}`}
                    placeholder={form.messagePlaceholder}
                    rows={7}
                  />
                  <span className={styles.fieldLine} aria-hidden="true" />
                </div>
              </FieldGroup>

              <motion.button
                type="submit"
                className={styles.submitButton}
                whileHover={{ y: -2, boxShadow: '0 22px 52px rgba(39,24,24,0.18)' }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 380, damping: 28 }}
              >
                {form.submitLabel}
                <motion.span
                  className={styles.submitIcon}
                  aria-hidden="true"
                  whileHover={{ x: 4 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                >
                  <ArrowRight aria-hidden="true" strokeWidth={1.8} />
                </motion.span>
              </motion.button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </section>
  );
}

/* ─── Main view ──────────────────────────────────────────────────────────── */

export function ContactPageView({ data }: ContactPageViewProps) {
  const ctaRef = useRef<HTMLElement>(null);
  const ctaInView = useInView(ctaRef, { once: true, margin: '-60px' });
  const [liveHours, setLiveHours] = useState(data.hours);

  useEffect(() => {
    let alive = true;
    let timeoutId: number | undefined;
    let intervalId: number | undefined;
    let removeLoadListener: (() => void) | undefined;

    async function refreshHours() {
      try {
        const response = await fetch(`/api/opening-hours?locale=${encodeURIComponent(data.locale)}`, { cache: 'no-cache' });
        if (!response.ok) return;
        const bundle = (await response.json()) as OpeningHoursBundle;
        if (!alive) return;
        setLiveHours({
          title: data.hours.title,
          todayLabel: data.hours.todayLabel,
          statusLabel: bundle.currentStatus.label,
          statusDetail: bundle.currentStatus.detail,
          isOpen: bundle.currentStatus.isOpen,
          message:
            data.locale === 'fr'
              ? bundle.messages.contactMessageFr || bundle.messages.homeMessageFr
              : null,
          rows: bundle.hours.map((hour) => ({
            dayOfWeek: hour.dayOfWeek,
            day: hour.dayLabel,
            value: hour.displayLabel,
            isClosed: hour.isClosed,
          })),
        });
      } catch {
        // Keep the last rendered hours visible if the realtime refresh fails.
      }
    }

    const startRefreshLoop = () => {
      timeoutId = window.setTimeout(() => {
        if (!alive) return;
        void refreshHours();
        intervalId = window.setInterval(refreshHours, REFRESH_INTERVAL_MS);
      }, INITIAL_REFRESH_DELAY_MS);
    };

    if (document.readyState === 'complete') {
      startRefreshLoop();
    } else {
      const onLoad = () => startRefreshLoop();
      window.addEventListener('load', onLoad, { once: true });
      removeLoadListener = () => window.removeEventListener('load', onLoad);
    }

    return () => {
      alive = false;
      removeLoadListener?.();
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, [data.hours.title, data.hours.todayLabel, data.locale]);

  return (
    <>
      <section className={styles.quickSection} aria-label={data.quickSectionAriaLabel}>
        <Stagger className={styles.quickGrid} stagger={0.1} delayChildren={0.04}>
          {data.quickCards.map((card) => (
            <StaggerChild key={card.id} y={28} duration={0.68}>
              <QuickCard card={card} />
            </StaggerChild>
          ))}
        </Stagger>
      </section>

      <InfoSection story={data.story} hours={liveHours} map={data.map} />

      <FormSection form={data.form} />

      <section ref={ctaRef} className={styles.ctaSection}>
        <motion.div
          className={styles.ctaInner}
          initial={{ opacity: 0, y: 18 }}
          animate={ctaInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.72, ease: EASE }}
        >
          <p className={styles.ctaTitle}>{data.cta.title}</p>
          <div className={styles.ctaLinks}>
            {data.cta.links.map((link, i) => (
              <motion.div
                key={link.id}
                initial={{ opacity: 0, y: 10 }}
                animate={ctaInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.55, delay: 0.1 + i * 0.08, ease: EASE }}
              >
                <Link href={link.href} className={styles.ctaLink}>
                  {link.label}
                  <span className={styles.ctaLinkLine} aria-hidden="true" />
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>
    </>
  );
}
